import { isAllowedUrl } from '@/lib/crm/utils/urlValidation';
import { applyLinkSafetyToDom } from '@/lib/email/linkSafety';

/** A mail provider whose web UI we can name in an "Open in …" affordance. */
export type EmailProviderFamily = 'gmail' | 'outlook';

/**
 * Map a provider string to the web UI it belongs to. The thread/trigger
 * endpoints serve the MailboxAPI name (`gmail`, `outlook_mail`,
 * `test_integration`); CRM events carry `outlook`; `ms365` is the legacy
 * trigger enum. Anything else (synthetic `test_integration`, an unknown
 * provider) has no nameable web UI → `null`.
 */
export function emailProviderFamily(
  provider: string | null | undefined
): EmailProviderFamily | null {
  switch (provider) {
    case 'gmail':
      return 'gmail';
    case 'ms365':
    case 'outlook_mail':
    case 'outlook':
      return 'outlook';
    default:
      return null;
  }
}

export interface ThreadExternalLink {
  family: EmailProviderFamily;
  href: string;
}

/**
 * Resolve where a thread's "Open in Gmail/Outlook" link should go, or `null`
 * when there is no real destination. The API-supplied `externalLink` is
 * validated against the http(s) allowlist (a `javascript:`/`data:` value must
 * never become an href). Only Gmail thread ids map to a buildable URL; an
 * Outlook thread without a web link, or a provider we can't name, gets no link
 * rather than a Gmail URL built from a foreign thread id.
 */
export function resolveThreadExternalLink(
  provider: string | null | undefined,
  threadId: string | null | undefined,
  externalLink: string | null | undefined
): ThreadExternalLink | null {
  const family = emailProviderFamily(provider);
  if (family === null) {
    return null;
  }
  const safeExternalLink =
    externalLink && isAllowedUrl(externalLink) ? externalLink : null;
  if (safeExternalLink) {
    return { family, href: safeExternalLink };
  }
  if (family === 'gmail' && threadId) {
    return {
      family,
      href: `https://mail.google.com/mail/u/0/#all/${threadId}`,
    };
  }
  return null;
}

/**
 * Format date to relative time or short date.
 *
 * `timeZone` is the display timezone (from `useDisplayTimezone()`) used when
 * the date is old enough to render as an absolute date rather than a
 * relative offset.
 */
export function formatDate(dateStr: string, timeZone: string): string {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    }
    if (diffMins < 60) {
      return `${diffMins}m ago`;
    }
    if (diffHours < 24) {
      return `${diffHours}h ago`;
    }
    if (diffDays < 7) {
      return `${diffDays}d ago`;
    }

    return date.toLocaleDateString('en-US', {
      timeZone,
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  } catch {
    return dateStr;
  }
}

/**
 * Normalize a raw email body to HTML and detect quoted content, without
 * hiding it. Pure — independent of whether quoted content is shown.
 */
export function parseEmailBody(bodyContent: string): {
  processedBody: string;
  hasQuoted: boolean;
} {
  const isPlainText = !/<[^>]+>/.test(bodyContent);

  let processedBody = bodyContent;
  if (isPlainText) {
    processedBody = bodyContent
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .join('<br>');
  }

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = processedBody;
  const hasQuoted =
    tempDiv.querySelectorAll('blockquote, .gmail_quote, .quoted-text').length >
    0;

  return { processedBody, hasQuoted };
}

/**
 * Build the display HTML for the iframe, hiding quoted content unless
 * showQuoted is true, and applying link safety.
 */
export function buildDisplayHtml(
  processedBody: string,
  showQuoted: boolean
): string {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = processedBody;

  if (!showQuoted) {
    tempDiv
      .querySelectorAll('blockquote, .gmail_quote, .quoted-text')
      .forEach(quote => {
        (quote as HTMLElement).style.display = 'none';
      });
  }

  applyLinkSafetyToDom(tempDiv);

  return tempDiv.innerHTML;
}
